import React from "react";
import { FormField } from "../../molecules/FormField/FormField";
import { FormFieldSelect } from "../../molecules/FormFieldSelect/FormFieldSelect";
import { Button } from "../../atoms/Button/Button";
import { Spinner } from "../../atoms/Spinner/Spinner";
import { Notification } from "../../molecules/Notification/Notification";
import useRegistration from "../../../features/registration/application/useRegistration";

interface RegistrationFormProps {
  referrerId?: string;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  referrerId,
}) => {
  const {
    handleSubmit,
    setFormData,
    clearRegistrationError,
    setFormDataError,
    isPhoneValid,
    data,
  } = useRegistration(referrerId);

  // Un manejador de cambios genérico para simplificar los inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormDataError({
      ...data.formDataError,
      [name]: false,
    });
    setFormData({
      ...data.formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <>
      <form className="card w-full max-w-screen-lg">
        <h2 className="text-2xl font-bold mb-10 mt-7 text-center">{process.env.REACT_APP_CAMPAIGN_TITLE}</h2>
        {data.loading && <Spinner size="small" />}
        {!data.loading && (
          <>
            <div className="w-full">
              <div className="flex md:flex-row flex-col md:gap-5">
                <FormField
                  label="Nombre completo"
                  id="nombre"
                  type="text"
                  placeholder="Tu nombre completo"
                  value={data.formData.name}
                  name="name"
                  error={data.formDataError.name}
                  onChange={handleChange}
                />

                <FormField
                  label="Celular"
                  id="phone"
                  type="phone"
                  placeholder="Tu celular"
                  value={data.formData.phoneShow}
                  error={data.formDataError.phone}
                  onChange={(phoneValue, countryData) => {
                    const isValid = isPhoneValid(phoneValue);
                    setFormData({
                      ...data.formData,
                      phoneShow: phoneValue,
                      phone: phoneValue.replace('+' + countryData?.country.dialCode, ""),
                      codeCountry: '+' + countryData?.country.dialCode || "",
                    });
                    setFormDataError({
                      ...data.formDataError,
                      phone: !isValid,
                    });
                  }}
                />
              </div>

              <div className="flex md:flex-row flex-col md:gap-5">
                <FormField
                  label="Correo Electrónico"
                  id="email"
                  type="email"
                  placeholder="tu@correo.com"
                  value={data.formData.email}
                  name="email"
                  error={data.formDataError.email}
                  onChange={handleChange}
                />
                {(process.env.REACT_APP_CAMPAIGN_SCOPE !== "REGIONAL") && (
                  <FormFieldSelect
                    label="Departamento"
                    id="department"
                    options={data.departments}
                    onChange={(event) => {
                      setFormData({
                        ...data.formData,
                        department: {
                          id: Number(event.target.value),
                          name:
                            data.departments.find(
                              (dep) => dep.id === Number(event.target.value)
                            )?.name || "",
                        },
                        city: { id: 0, name: "" },
                      });
                    }}
                  />
                )}
              </div>

              <div className="flex md:flex-row flex-col md:gap-5">
                <FormFieldSelect
                  label="Ciudad"
                  id="city"
                  options={data.cities}
                  onChange={(event) => {
                    setFormData({
                      ...data.formData,
                      city: {
                        id: Number(event.target.value),
                        name:
                          data.cities.find(
                            (city) => city.id === Number(event.target.value)
                          )?.name || "",
                      },
                    });
                  }}
                />
                <FormFieldSelect
                  label="Rango de Edad"
                  id="age"
                  options={data.ageRanges}
                  onChange={(event) => {
                    setFormData({
                      ...data.formData,
                      ageRange: event.target.value,
                    });
                  }}
                />
              </div>

              <div className="flex md:flex-row flex-col md:gap-5">
                <FormFieldSelect
                  label="Genero"
                  id="gender"
                  options={data.genders}
                  onChange={(event) => {
                    setFormData({
                      ...data.formData,
                      gender: event.target.value,
                    });
                  }}
                />
                {(data.topicsInterest.length > 0) && (
                  <FormFieldSelect
                    label="Tema de interés"
                    id="topicInterest"
                    options={data.topicsInterest}
                    onChange={(event) => {
                      setFormData({
                        ...data.formData,
                        topicInterest: event.target.value,
                      });
                    }}
                  />
                )}
              </div>

              <FormField
                label={
                  <span>
                    Acepto la{" "}
                    <a
                      href={"/policy-data"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-tertiary hover:underline"
                    >
                      política de tratamiento de datos
                    </a>
                  </span>
                }
                id="policy"
                type="checkbox"
                name="policyAccepted"
                checked={data.formData.policyAccepted || false}
                error={data.formDataError.policyAccepted}
                onChange={handleChange}
              />
            </div>

            <div className="md:inline-flex md:items-center mt-4 w-full md:w-auto">
              <Button color="primary" type="button" onClick={handleSubmit}>
                Registrarse
              </Button>
            </div>
          </>
        )}
      </form>
      {data.error && (
        <Notification
          message={data.error || ""}
          type="error"
          onClose={clearRegistrationError}
        />
      )}
    </>
  );
};
